import { Fragment } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronUpIcon, ChevronDownIcon, ChevronRightIcon } from 'lucide-react';
import {
  Table, TableHeader, TableBody, TableHead, TableRow, TableCell,
} from '@/components/ui';
import { SubscriptionStatusBadge } from './subscription-status-badge';
import { SubscriptionDetailCard } from './subscription-detail-card';
import { cn, formatDate, truncate } from '@/lib/utils';
import { MerchantCell } from '@/components/shared/merchant-cell';
import { ServiceCell } from '@/components/shared/service-cell';
import type { SortState } from '@/hooks/use-sort-state';
import type { AdminSubscription } from '@/types/admin-subscription';

/** Sortierbare Spalten der Subscription-Tabelle (Spec 0222 B5: nur real gelieferte Felder) */
export type SubscriptionSortColumn = 'createdAt' | 'status' | 'renewalCount' | 'lastRenewalAt';

interface SubscriptionTableProps {
  subscriptions: AdminSubscription[];
  expandedId: string | null;
  onToggleExpand: (subscriptionId: string) => void;
  onTerminate?: (subscriptionId: string) => void;
  isTerminating: boolean;
  sort?: SortState<SubscriptionSortColumn>;
  onToggleSort?: (column: SubscriptionSortColumn) => void;
  /** Spec 0220: Merchant-Spalte nur fuer Platform-Rollen (Default: sichtbar). */
  showMerchant?: boolean;
}

export function SubscriptionTable({
  subscriptions,
  expandedId,
  onToggleExpand,
  onTerminate,
  isTerminating,
  sort,
  onToggleSort,
  showMerchant = true,
}: SubscriptionTableProps) {
  const { t } = useTranslation('subscriptions');

  if (subscriptions.length === 0) {
    return (
      <div className="py-12 text-center text-muted-foreground">
        {t('list.empty')}
      </div>
    );
  }

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t('list.columns.subscriptionId')}</TableHead>
          {showMerchant && <TableHead>{t('list.columns.merchant')}</TableHead>}
          <TableHead>{t('list.columns.service')}</TableHead>
          <SortableHead
            label={t('list.columns.status')}
            column="status"
            sort={sort}
            onToggle={onToggleSort}
          />
          <TableHead>{t('list.columns.operator')}</TableHead>
          <SortableHead
            label={t('list.columns.charges')}
            column="renewalCount"
            sort={sort}
            onToggle={onToggleSort}
          />
          <SortableHead
            label={t('list.columns.lastRenewal')}
            column="lastRenewalAt"
            sort={sort}
            onToggle={onToggleSort}
          />
          <SortableHead
            label={t('list.columns.created')}
            column="createdAt"
            sort={sort}
            onToggle={onToggleSort}
          />
        </TableRow>
      </TableHeader>
      <TableBody>
        {subscriptions.map((sub) => (
          <Fragment key={sub.subscriptionId}>
            <TableRow
              className="cursor-pointer touch-manipulation hover:bg-muted/50 coarse:h-14"
              onClick={() => onToggleExpand(sub.subscriptionId)}
              aria-expanded={expandedId === sub.subscriptionId}
            >
              {/* Spec 514: sichtbare Expand-Affordanz — Chevron rotiert im offenen
                  Zustand; dekorativ (aria-hidden), der Zustand haengt als
                  aria-expanded an der Zeile selbst. */}
              <TableCell className="font-mono text-sm">
                <span className="flex items-center gap-1.5">
                  <ChevronRightIcon
                    aria-hidden="true"
                    className={cn(
                      'h-4 w-4 shrink-0 text-muted-foreground transition-transform',
                      expandedId === sub.subscriptionId && 'rotate-90',
                    )}
                  />
                  {truncate(sub.subscriptionId, 16)}
                </span>
              </TableCell>
              {showMerchant && <TableCell><MerchantCell merchantId={sub.merchantId} /></TableCell>}
              <TableCell>
                <ServiceCell serviceId={sub.serviceId} />
              </TableCell>
              <TableCell>
                <SubscriptionStatusBadge status={sub.status} />
              </TableCell>
              <TableCell>{sub.operatorId ?? '-'}</TableCell>
              <TableCell>{sub.renewalCount ?? '—'}</TableCell>
              <TableCell>{formatDate(sub.lastRenewalAt)}</TableCell>
              <TableCell>{formatDate(sub.createdAt)}</TableCell>
            </TableRow>
            {expandedId === sub.subscriptionId && (
              <TableRow>
                <TableCell colSpan={showMerchant ? 8 : 7} className="p-0">
                  <SubscriptionDetailCard
                    subscription={sub}
                    onTerminate={onTerminate ? () => onTerminate(sub.subscriptionId) : undefined}
                    isTerminating={isTerminating}
                  />
                </TableCell>
              </TableRow>
            )}
          </Fragment>
        ))}
      </TableBody>
    </Table>
  );
}

/** Sortierbare Spaltenkoepfe mit Pfeil-Indikator */
function SortableHead<T extends string>({
  label,
  column,
  sort,
  onToggle,
}: {
  label: string;
  column: T;
  sort?: SortState<T>;
  onToggle?: (column: T) => void;
}) {
  const isActive = sort?.column === column;

  // Spec 514: aria-sort liegt auf dem <th>, das interaktive Element ist ein die Zelle
  // voll ausfuellender <button> (Tastatur + Touch); Toggle-Reihenfolge unveraendert.
  // Bewusst NICHT mit der TX-Variante zu einer Shared-Komponente extrahiert (Scope).
  return (
    <TableHead
      aria-sort={isActive ? (sort.direction === 'asc' ? 'ascending' : 'descending') : undefined}
      className="p-0"
    >
      <button
        type="button"
        onClick={() => onToggle?.(column)}
        className="flex h-10 w-full cursor-pointer touch-manipulation select-none items-center gap-1 px-2 hover:bg-muted/50 coarse:min-h-11"
      >
        {label}
        {isActive && (
          sort.direction === 'asc'
            ? <ChevronUpIcon className="h-3.5 w-3.5" />
            : <ChevronDownIcon className="h-3.5 w-3.5" />
        )}
      </button>
    </TableHead>
  );
}
